import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

/**
 * A small confirmation modal on the native <dialog>, so focus is trapped and Esc cancels.
 * Focus starts on Cancel, so a stray Enter never confirms a destructive action.
 */
export default function ConfirmDialog({ open, title, message, confirmLabel, busyLabel, busy, error, onConfirm, onCancel }) {
    const dialogRef = useRef(null);

    useEffect(() => {
        const dialog = dialogRef.current;
        if (!dialog) return;
        if (open && !dialog.open) dialog.showModal();
        if (!open && dialog.open) dialog.close();
    }, [open]);

    return createPortal(
        <dialog
            ref={dialogRef}
            aria-labelledby="confirm-title"
            className="tryon-agent m-auto w-[min(92vw,26rem)] rounded-2xl border-0 bg-white p-0 shadow-[0_0_0_1px_var(--hairline)] backdrop:bg-[var(--overlay)]"
            onCancel={(event) => {
                event.preventDefault();
                if (!busy) onCancel();
            }}
            onClick={(event) => {
                if (event.target === event.currentTarget && !busy) onCancel();
            }}
        >
            {open && (
                <div className="p-6 font-Mulish">
                    <h2 id="confirm-title" className="text-lg font-semibold text-secondary">
                        {title}
                    </h2>
                    <p className="mt-2 text-sm leading-relaxed text-graphite">{message}</p>
                    {error && <p className="mt-3 rounded-xl bg-primary-50 px-3 py-2 text-sm text-primary-800">{error}</p>}
                    <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                        <button
                            type="button"
                            autoFocus
                            disabled={busy}
                            onClick={onCancel}
                            className="rounded-full border border-primary-200 bg-white px-5 py-2.5 text-sm text-primary-700 transition-colors hover:bg-primary-50 focus:outline-none focus-visible:shadow-[var(--ring)] disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            disabled={busy}
                            onClick={onConfirm}
                            className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors focus:outline-none focus-visible:shadow-[var(--ring)] ${
                                busy ? "cursor-default bg-primary-100 text-primary-800" : "bg-primary-700 text-white shadow-[0_12px_28px_-10px_rgba(190,49,32,0.65)] hover:bg-primary-800"
                            }`}
                        >
                            {busy ? busyLabel : confirmLabel}
                        </button>
                    </div>
                </div>
            )}
        </dialog>,
        document.body
    );
}
